import { useState, useRef } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Mic button that records a short voice note, sends it to the server for
// transcription (OpenAI Whisper) and hands back the text. The recording is
// discarded — only the words are kept. Tap to start, tap again to stop.
export function VoiceNoteButton({ onTranscript, className = "", disabled = false }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const handleClick = async () => {
    if (state === "transcribing") return;
    if (state === "recording") {
      if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice notes aren't supported on this device");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = ["audio/webm", "audio/mp4", "audio/mpeg"].find(
        (m) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)
      ) || "";
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        cleanupStream();
        const type = recorder.mimeType || mime || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        if (!blob.size) { setState("idle"); return; }
        setState("transcribing");
        try {
          const ext = type.includes("mp4") ? "mp4" : type.includes("mpeg") ? "mp3" : "webm";
          const fd = new FormData();
          fd.append("audio", blob, `note.${ext}`);
          const { data } = await api.post("/timesheets/transcribe", fd, {
            timeout: 60000,
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (data?.text || "").trim();
          if (text) onTranscript(text);
          else toast.message("Didn't catch anything — give it another go");
        } catch {
          toast.error("Couldn't transcribe that — please try again");
        } finally {
          setState("idle");
        }
      };
      recorder.start();
      recorderRef.current = recorder;
      setState("recording");
    } catch {
      cleanupStream();
      toast.error("Microphone was blocked. Allow mic access to dictate notes.");
      setState("idle");
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || state === "transcribing"}
      aria-label={state === "recording" ? "Stop recording" : "Dictate notes"}
      data-testid="voice-note-btn"
      className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-2.5 py-1.5 transition-colors disabled:opacity-60 ${
        state === "recording"
          ? "bg-red-500 text-white animate-pulse"
          : "bg-[#FF5F15]/10 text-[#FF5F15] hover:bg-[#FF5F15]/20"
      } ${className}`}
    >
      {state === "transcribing" ? (
        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Transcribing…</>
      ) : state === "recording" ? (
        <><Square className="w-3.5 h-3.5" fill="currentColor" /> Stop</>
      ) : (
        <><Mic className="w-3.5 h-3.5" /> Dictate</>
      )}
    </button>
  );
}
